import {
  asIssueFieldSelection,
  type IssueFieldSelection,
  type IssueFieldSelectionValues,
  type IssueFieldSingleSelectOption,
  type IssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import type {IssueField} from '@github-ui/item-picker/IssueFieldPicker'

import type {SafeFieldValue} from '../components/fields/IssueFieldEditors'
import {formatDateString} from './date-formatting'

// type that is used when we add a text field during issue creation
type IssueTextFieldWithValue = {
  field: IssueField & {dataType?: 'TEXT'}
  value: string
}

// type that is used when we add a single select field during issue creation
type IssueSingleSelectFieldWithValue = {
  field: IssueField & {dataType?: 'SINGLE_SELECT'}
  value: IssueFieldSingleSelectOption | null
}

type IssueDateFieldWithValue = {
  field: IssueField & {dataType?: 'DATE'}
  value: Date | null
}

export type IssueNumberFieldWithValue = {
  field: IssueField & {dataType?: 'NUMBER'}
  value: number | null
}

export type IssueFieldWithUnsavedValue = (
  | IssueTextFieldWithValue
  | IssueSingleSelectFieldWithValue
  | IssueDateFieldWithValue
  | IssueNumberFieldWithValue
) & {
  readonly?: boolean
}

export function isTextField(field: IssueField): field is IssueField & {dataType: 'TEXT'} {
  return field.dataType === 'TEXT'
}

export function isSingleSelectField(field: IssueField): field is IssueField & {dataType: 'SINGLE_SELECT'} {
  return field.dataType === 'SINGLE_SELECT'
}

export function isDateField(field: IssueField): field is IssueField & {dataType: 'DATE'} {
  return field.dataType === 'DATE'
}

export function isNumberField(field: IssueField): field is IssueField & {dataType: 'NUMBER'} {
  return field.dataType === 'NUMBER'
}

export function isIssueFieldWithUnsavedValue(field: IssueFieldWithUnsavedValue): field is IssueTextFieldWithValue {
  return isTextField(field.field)
}

export function isSingleSelectFieldWithValue(
  field: IssueFieldWithUnsavedValue,
): field is IssueSingleSelectFieldWithValue {
  return isSingleSelectField(field.field) && field.value !== null
}

// Check if two field values are equal based on field type
export function areFieldValuesEqual(
  initialField: SafeFieldValue | null | undefined,
  currentField: SafeFieldValue | null | undefined,
): boolean {
  if (initialField?.field?.id !== currentField?.field?.id) return false

  if (!initialField?.field || !currentField?.field) return false

  const fieldForTypeCheck = initialField.field as IssueField

  if (isTextField(fieldForTypeCheck) || isDateField(fieldForTypeCheck)) {
    return initialField?.value === currentField?.value
  }

  if (isNumberField(fieldForTypeCheck)) {
    return initialField?.numberValue === currentField?.numberValue
  }

  if (isSingleSelectField(fieldForTypeCheck)) {
    return initialField?.name === currentField?.name
  }

  return true
}

// Categorize field changes into added, updated, and removed fields
export function getFieldChanges(
  initialValues: SafeFieldValue[],
  currentValues: SafeFieldValue[],
): {
  addedFields: SafeFieldValue[]
  updatedFields: SafeFieldValue[]
  removedFields: SafeFieldValue[]
} {
  const addedFields = currentValues.filter(
    fieldValue => !initialValues.some(initialFieldValue => initialFieldValue?.field?.id === fieldValue?.field?.id),
  )

  const updatedFields = currentValues.filter(fieldValue =>
    initialValues.some(
      initialFieldValue =>
        initialFieldValue?.field?.id === fieldValue?.field?.id && !areFieldValuesEqual(initialFieldValue, fieldValue),
    ),
  )

  const removedFields = initialValues.filter(
    initialFieldValue => !currentValues.some(fv => fv?.field?.id === initialFieldValue?.field?.id),
  )

  return {addedFields, updatedFields, removedFields}
}

// Create a cleared field value
export function createClearedFieldValue(
  fieldValue: SafeFieldValue | null | undefined,
): SafeFieldValue | null | undefined {
  if (!fieldValue || !fieldValue.field) return fieldValue

  // For all field types, return cleared value
  return toIssueFieldWithUnsavedValue(fieldValue.field, null) as SafeFieldValue
}

export function toIssueFieldWithUnsavedValue(
  selectedField: IssueField | IssueFieldSelection,
  value: IssueFieldSelectionValues,
): IssueFieldWithUnsavedValue {
  const field = asIssueFieldSelection(selectedField)
  switch (field.dataType) {
    case 'text':
      return {
        field: {...field, dataType: 'TEXT'},
        value: (value || '') as string,
      }
    case 'single_select':
      return {
        field: {...field, dataType: 'SINGLE_SELECT'},
        value: value as IssueFieldSingleSelectOption | null,
      }
    case 'date':
      return {
        field: {...field, dataType: 'DATE'},
        value: value as Date | null,
      }
    case 'number': {
      return {
        field: {...field, dataType: 'NUMBER'},
        value: value as number | null,
      }
    }
  }
}

export function toIssueFieldWithValue(
  selectedField: IssueField | IssueFieldSelection,
  value: IssueFieldSelectionValues,
  readonly?: boolean,
) {
  const field = asIssueFieldSelection(selectedField)
  switch (field.dataType) {
    case 'text':
      return {
        field: {...field, dataType: 'TEXT'},
        value: value as string,
        readonly,
      } as IssueTextFieldWithValue
    case 'single_select':
      return {
        field: {...field, dataType: 'SINGLE_SELECT'},
        value: value as IssueFieldSingleSelectOption | null,
        readonly,
      } as IssueSingleSelectFieldWithValue
    case 'date':
      return {
        field: {...field, dataType: 'DATE'},
        value: value as Date | null,
        readonly,
      } as IssueDateFieldWithValue
    case 'number': {
      // If value is null or empty string, keep it as null (for deletion)
      if (value === null || value === '') {
        return {
          field: {...field, dataType: 'NUMBER'},
          value: null,
          readonly,
        } as IssueNumberFieldWithValue
      }
      const number = Number(value)
      return {
        field: {...field, dataType: 'NUMBER'},
        value: isNaN(number) ? 0 : number,
        readonly,
      } as IssueNumberFieldWithValue
    }
  }
}

export function getDeleteValue(fieldType: IssueFieldType, value?: string) {
  switch (fieldType) {
    case 'text':
      return value === ''
    case 'single_select':
      return value === undefined
    case 'date':
      return value === undefined
    case 'number':
      return value === ''
    default:
      throw new Error(`Unsupported field type: ${fieldType}`)
  }
}

export function getIssueFieldSelectionValue(fieldType: IssueFieldType, value: IssueFieldSelectionValues) {
  switch (fieldType) {
    case 'text':
      return value as string | undefined
    case 'single_select': {
      const singleSelectOption = value as IssueFieldSingleSelectOption | null
      return singleSelectOption && !!singleSelectOption.id ? String(singleSelectOption.id) : undefined
    }
    case 'date':
      return formatDateForStorage(value as Date | null) || undefined
    case 'number': {
      return value?.toString()
    }
  }
}

export function getIssueFieldSelectionPresentationValue(fieldType: IssueFieldType, value: IssueFieldSelectionValues) {
  if (fieldType === 'single_select') {
    const singleSelectOption = value as IssueFieldSingleSelectOption | null
    return singleSelectOption && !!singleSelectOption.name ? String(singleSelectOption.name) : undefined
  } else if (fieldType === 'date') {
    const dateValue = value as Date | null
    return dateValue ? formatDateString(dateValue) : undefined
  }
  return getIssueFieldSelectionValue(fieldType, value)
}

export function isDateFieldWithValue(field: IssueFieldWithUnsavedValue): field is IssueDateFieldWithValue {
  return isDateField(field.field) && field.value !== null
}

// Helper function to format dates consistently and avoid timezone issues
// The function sets the time to noon UTC (12:00 PM) to normalize the date.
// Noon UTC is chosen because it minimizes the risk of date shifts caused by timezone differences.
// For example, if a date is stored without a specific time, converting it across timezones could
// result in the date being shifted forward or backward. By fixing the time at noon UTC, we ensure
// that the date remains consistent regardless of the user's timezone.
// This function is intended for formatting dates before storing them in a database or API.
export function formatDateForStorage(date: Date | null): string | null {
  if (!date) return null
  const normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0))
  return normalizedDate.toISOString()
}
